import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Share {
  id: string;
  memberId: string;
  memberName: string;
  sharesOwned: number;
  shareValue: number;
  totalValue: number;
  lastUpdated: string;
}

interface Dividend {
  id: string;
  period: string;
  totalAmount: number;
  amountPerShare: number;
  totalShares: number;
  paymentDate: string;
  status: 'paid' | 'pending' | 'processing';
  distributions: Array<{
    memberId: string;
    memberName: string;
    sharesOwned: number;
    dividendAmount: number;
    paid: boolean;
  }>;
}

const ChamaSharesDividends: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [activeTab, setActiveTab] = useState('shares');
  const [shares, setShares] = useState<Share[]>([]);
  const [dividends, setDividends] = useState<Dividend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (chamaId) {
      loadSharesData();
      loadDividendsData();
    }
  }, [chamaId]);

  const loadSharesData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/shares`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setShares(data.data);
        } else {
          setShares([]);
        }
      }
    } catch (error) {
      console.error('Failed to load shares data:', error);
      setShares([]);
    }
  };

  const loadDividendsData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/dividends`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setDividends(data.data);
        } else {
          setDividends([]);
        }
      }
    } catch (error) {
      console.error('Failed to load dividends data:', error);
      setDividends([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getDividendStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return colors.success;
      case 'pending': return colors.warning;
      case 'processing': return colors.info;
      default: return colors.textSecondary;
    }
  };

  const getDividendStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return '✅';
      case 'pending': return '⏳';
      case 'processing': return '⚙️';
      default: return '❓';
    }
  };

  const renderSharesTab = () => (
    <div style={styles.tabContent}>
      {/* Shares Summary */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📊</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {shares.reduce((sum, share) => sum + share.sharesOwned, 0)}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Shares</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>💰</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {formatCurrency(shares.reduce((sum, share) => sum + share.totalValue, 0))}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Value</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>👥</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {shares.length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Shareholders</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📈</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {shares.length > 0 ? formatCurrency(shares.reduce((sum, share) => sum + share.totalValue, 0) / shares.length) : formatCurrency(0)}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Avg per Member</div>
          </div>
        </div>
      </div>

      {/* Shares List */}
      <div style={styles.sharesList}>
        {shares.length > 0 ? (
          shares.map(renderShareCard)
        ) : (
          <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.emptyIcon}>📊</div>
            <div style={{ ...styles.emptyTitle, color: colors.text }}>No shares data available</div>
            <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
              Share ownership information will appear here once shares are issued.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDividendsTab = () => (
    <div style={styles.tabContent}>
      {/* Dividends Summary */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>💰</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {formatCurrency(dividends.reduce((sum, dividend) => sum + dividend.totalAmount, 0))}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Dividends Paid</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📅</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {dividends.filter(d => d.status === 'paid').length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Dividend Periods</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>⏳</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {dividends.filter(d => d.status === 'pending').length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Pending Payments</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📈</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {dividends.length > 0 ? formatCurrency(dividends.reduce((sum, d) => sum + d.amountPerShare, 0) / dividends.length) : formatCurrency(0)}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Avg per Share</div>
          </div>
        </div>
      </div>

      {/* Dividends List */}
      <div style={styles.dividendsList}>
        {dividends.length > 0 ? (
          dividends.map(renderDividendCard)
        ) : (
          <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.emptyIcon}>💰</div>
            <div style={{ ...styles.emptyTitle, color: colors.text }}>No dividends data available</div>
            <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
              Dividend payment history will appear here once dividends are distributed.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderShareCard = (share: Share) => (
    <div key={share.id} style={{ ...styles.shareCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.shareHeader}>
        <div style={styles.memberInfo}>
          <div style={styles.memberAvatar}>
            <span style={{ fontSize: '16px' }}>
              {share.memberName.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          <div style={styles.memberDetails}>
            <div style={{ ...styles.memberName, color: colors.text }}>{share.memberName}</div>
            <div style={{ ...styles.lastUpdated, color: colors.textSecondary }}>
              Last updated: {new Date(share.lastUpdated).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.shareStats}>
        <div style={styles.shareStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{share.sharesOwned}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Shares Owned</div>
        </div>
        <div style={styles.shareStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{formatCurrency(share.shareValue)}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Value per Share</div>
        </div>
        <div style={styles.shareStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{formatCurrency(share.totalValue)}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Value</div>
        </div>
      </div>
    </div>
  );

  const renderDividendCard = (dividend: Dividend) => (
    <div key={dividend.id} style={{ ...styles.dividendCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.dividendHeader}>
        <div style={styles.dividendInfo}>
          <div style={{ ...styles.dividendPeriod, color: colors.text }}>{dividend.period}</div>
          <div style={{ ...styles.dividendDate, color: colors.textSecondary }}>
            Paid on {new Date(dividend.paymentDate).toLocaleDateString()}
          </div>
        </div>
        <div style={{ ...styles.dividendStatus, backgroundColor: getDividendStatusColor(dividend.status) + '20', border: `1px solid ${getDividendStatusColor(dividend.status)}` }}>
          <span style={{ fontSize: '16px', marginRight: '4px' }}>{getDividendStatusIcon(dividend.status)}</span>
          <span style={{ ...styles.statusText, color: getDividendStatusColor(dividend.status) }}>
            {dividend.status.charAt(0).toUpperCase() + dividend.status.slice(1)}
          </span>
        </div>
      </div>

      <div style={styles.dividendStats}>
        <div style={styles.dividendStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{formatCurrency(dividend.totalAmount)}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Paid</div>
        </div>
        <div style={styles.dividendStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{formatCurrency(dividend.amountPerShare)}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Per Share</div>
        </div>
        <div style={styles.dividendStat}>
          <div style={{ ...styles.statValue, color: colors.text }}>{dividend.totalShares}</div>
          <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Shares</div>
        </div>
      </div>

      {/* Distribution Details */}
      <div style={styles.distributionSection}>
        <div style={{ ...styles.distributionTitle, color: colors.textSecondary }}>Distribution Details</div>
        <div style={styles.distributionList}>
          {dividend.distributions.slice(0, 3).map((dist, index) => (
            <div key={dist.memberId} style={styles.distributionItem}>
              <div style={styles.distributionMember}>{dist.memberName}</div>
              <div style={styles.distributionShares}>{dist.sharesOwned} shares</div>
              <div style={styles.distributionAmount}>{formatCurrency(dist.dividendAmount)}</div>
              <div style={{ ...styles.distributionStatus, color: dist.paid ? colors.success : colors.warning }}>
                {dist.paid ? '✅ Paid' : '⏳ Pending'}
              </div>
            </div>
          ))}
          {dividend.distributions.length > 3 && (
            <div style={{ ...styles.moreDistributions, color: colors.textSecondary }}>
              +{dividend.distributions.length - 3} more distributions
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading shares & dividends...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Shares & Dividends"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            <button
              onClick={() => setActiveTab('shares')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'shares' ? colors.primary : 'transparent',
                color: activeTab === 'shares' ? 'white' : colors.text,
              }}
            >
              📊 Shares
            </button>
            <button
              onClick={() => setActiveTab('dividends')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'dividends' ? colors.primary : 'transparent',
                color: activeTab === 'dividends' ? 'white' : colors.text,
              }}
            >
              💰 Dividends
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'shares' ? renderSharesTab() : renderDividendsTab()}

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button style={{ ...styles.actionButton, backgroundColor: colors.primary }}>
              Issue Shares
            </button>
            <button style={{ ...styles.actionButton, backgroundColor: colors.success }}>
              Pay Dividends
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  tabContainer: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    flex: 1,
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    border: 'none',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  tabContent: {
    // Container for tab content
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  sharesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  shareCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  shareHeader: {
    marginBottom: spacing.md,
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  memberDetails: {
    flex: 1,
  },
  memberName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  lastUpdated: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  shareStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
  },
  shareStat: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  dividendsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  dividendCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  dividendHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  dividendInfo: {
    flex: 1,
  },
  dividendPeriod: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  dividendDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  dividendStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  dividendStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  dividendStat: {
    textAlign: 'center',
  },
  distributionSection: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: spacing.md,
  },
  distributionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  distributionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  distributionItem: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: spacing.sm,
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.sm,
  },
  distributionMember: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  distributionShares: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
  },
  distributionAmount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    textAlign: 'center',
  },
  distributionStatus: {
    fontSize: typography.fontSize.sm,
    textAlign: 'right',
  },
  moreDistributions: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    padding: spacing.sm,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  actionButtons: {
    display: 'flex',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  actionButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
};

export default ChamaSharesDividends;